import {
  createTheme,
  responsiveFontSizes,
  type Theme,
  type ThemeOptions,
} from '@mui/material/styles';
import tdcTheme from './tdc';
import { tdcFontFaces } from './tdc/theme/global/baseLine';
import { sequential, touchTargetMinimum } from './tokens';

/**
 * The MUI theme: the TDC brand theme, with the handful of overrides this application needs.
 *
 * The base is vendored from tdc-managed-cloud (packages/themes/src/tdc) — see
 * docs/beslutninger/2026-08-21-tdc-tema.md for why it is a copy rather than a dependency.
 * Everything below is layered on top the same way the reference app does it, so the brand
 * decisions stay in one place and ours stay visible as deltas.
 *
 * Light only. The TDC palette hardcodes a white background and black text, and there is no
 * sanctioned dark variant to derive from.
 *
 * Chart colours deliberately do NOT come from this palette. They live in ./tokens, because the
 * dependency bands and SEAL levels are an ordered scale where the colour carries meaning —
 * substituting brand colours would make the ramp say something different.
 */
export function buildTheme(): Theme {
  // responsiveFontSizes, as the reference app does. TDC's type scale is built for marketing
  // pages — h1 is 80px — and this is the brand's own way of scaling it down on smaller screens.
  // It matters here: the whole flow runs on a 1024px iPad.
  return responsiveFontSizes(
    createTheme(tdcTheme as ThemeOptions, {
      components: {
        MuiCssBaseline: {
          // Composed rather than replaced: styleOverrides is a single value, so assigning here
          // would drop the @font-face block and take the brand fonts with it.
          styleOverrides: `
          ${tdcFontFaces}

          :focus-visible {
            outline: 3px solid ${sequential[2]};
            outline-offset: 2px;
          }

          /* The customer makes their own PDF from the browser (specification §9). */
          @media print {
            nav, header button, .no-print { display: none !important; }
            body { background: #fff; color: #000; }
            .print-block { break-inside: avoid; }
          }
        `,
        },

        // 44 px minimum touch target — the whole flow is run one-handed on an iPad at a stand
        // (specification §8.4). TDC's own button sizing is close but not guaranteed to clear it.
        MuiButton: {
          styleOverrides: {
            root: { minHeight: touchTargetMinimum },
          },
        },
        MuiButtonBase: {
          styleOverrides: {
            root: { minHeight: touchTargetMinimum },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            input: { minHeight: touchTargetMinimum - 16 },
          },
        },

        MuiTextField: { defaultProps: { fullWidth: true } },
      },
    }),
  );
}
